import React from 'react';

import RoundedButtonWithArrow from '@shared/components/inputs/buttons/RoundedButtonWithArrow';
import EstimateGaugeBar from '@shared/components/charts/estimate-gauge-bar';
import { TAGGED_GROUP } from '@shared/constants/MetricsTypeConstants';
import ImageConstants from '@shared/constants/ImageConstants';
import { GOOGLE_BANNER_EXPERIMENTS_BACKGROUND } from '@shared/constants/BackgroundModalColors';
import FullScreenModal from '@shared/components/modals/FullScreenModal';
import ModalHeader from '@shared/components/modals/headers/generic-card/ModalHeader';
import getColorByRange from '@shared/utils/colors';
import ModalContent from '@shared/components/modals/components/ModalContent';
import RoundedSquareAccordion from '@shared/components/accordions/rounded-square/RoundedSquareAccordion';
import LoadingSkeleton from '@shared/components/loading/LoadingSkeleton';
import TEST_ID from '@shared/utils/_tests/testIds.constants';
import { NoTranslate } from '@shared/components/no-translate/no-translate';
import { SEGMENTS_MODAL_TEXTS } from './segments-modal.constants';
import * as S from './segments-modal-container.styles';

interface TagsGroupModalInfo {
    handleToggleTagsGroupModal: () => void,
    handleChangeTagGroupModalField: () => void,
    isCreating: boolean,
    isOpen: boolean,
    modalHeaderData: () => void,
    toggleSwitchButton: (boolean) => void,
    gaugePercentage?: number,
    handleClickSaveTags: {
        handleFunction: () => void,
        isButtonDisable: boolean,
    },
    tagsGroup: any,
    isFbTaggedByAdSet: boolean,
    renderTagInputComponent: () => void,
    isLoadingCampaigns: boolean,
}

const defaultProps = {
    isOpen: false,
    isCreating: null,
    modalHeaderData: null,
    isFbTaggedByAdSet: true,
    gaugePercentage: 0,
    isLoadingCampaigns: false,
};

const getModalHeaderData = (modalHeaderData) => {
    const formattedModalHeader = {
        background: {
            BACKGROUND: `url('${ImageConstants.TAGSGROUP.BACKGROUND.SRC}') center center/cover no-repeat,
                ${GOOGLE_BANNER_EXPERIMENTS_BACKGROUND} center center/cover no-repeat;`,
            IMAGES: [{
                backgroundImage: ImageConstants.TAGSGROUP.ILLUSTRATION.SRC,
                styles: {
                    top: '75px',
                    right: 'calc(55% + 300px)',
                },
            },
            ],
            backgroundImage: '',
        },
        shouldDisplayParetoLogo: true,
        title: modalHeaderData,
        subtitle: () => (
            <S.HeaderSubtitle>
                {SEGMENTS_MODAL_TEXTS.headerLabel}
            </S.HeaderSubtitle>
        ),
    };

    return { ...formattedModalHeader };
};

export const SegmentModal = ({
    handleToggleTagsGroupModal,
    handleChangeTagGroupModalField,
    isCreating,
    isOpen,
    modalHeaderData,
    tagsGroup,
    gaugePercentage,
    handleClickSaveTags,
    renderTagInputComponent,
    isLoadingCampaigns,
}: TagsGroupModalInfo) => {
    const { createLabel, saveLabel } = SEGMENTS_MODAL_TEXTS;

    return (
        <FullScreenModal
            iconColor="#ffffff"
            isNewModal
            isOpen={isOpen}
            onClose={() => handleToggleTagsGroupModal()}
        >
            <ModalHeader
                data={getModalHeaderData(modalHeaderData)}
                account={{ name: '', type: '' }}
            >
                <S.GaugerWrapper>
                    { isLoadingCampaigns
                        ? <LoadingSkeleton count={1} height="20px" width="520px" />
                        : (
                            <>
                                <EstimateGaugeBar
                                    isHover={false}
                                    isNewOverview
                                    metric={TAGGED_GROUP.campaigns_tagged.value}
                                    percentage={gaugePercentage.toFixed(2)}
                                    gaugeColor={getColorByRange(gaugePercentage, [50, 100])}
                                />
                                <S.GaugeLabel>
                                    {SEGMENTS_MODAL_TEXTS.gaugeLabel}
                                </S.GaugeLabel>
                            </>
                        )
                    }
                </S.GaugerWrapper>
            </ModalHeader>
            <ModalContent
                isNewModal
                styles={{ 'margin-top': '-120px' }}
            >
                { isLoadingCampaigns
                    ? (
                        <div data-testid={TEST_ID.MODAL.LOADING_SKELETON}>
                            <LoadingSkeleton count={8} height="58px" />
                        </div>
                    )
                    : (
                        <>
                            <S.InfoWrapper>
                                <S.AccordionWrapper>
                                    <NoTranslate>
                                        <RoundedSquareAccordion
                                            accordionItems={tagsGroup}
                                            readOnly={false}
                                            onChange={handleChangeTagGroupModalField}
                                            renderContent={renderTagInputComponent}
                                        />
                                    </NoTranslate>
                                </S.AccordionWrapper>
                            </S.InfoWrapper>
                            <RoundedButtonWithArrow
                                label={isCreating ? createLabel : saveLabel}
                                styles={S.buttonStyles}
                                onClick={handleClickSaveTags.handleFunction}
                                isDisabled={handleClickSaveTags.isButtonDisable}
                            />
                        </>
                    )
                }
            </ModalContent>

        </FullScreenModal>
    );
};

SegmentModal.defaultProps = defaultProps;
